import {
  DocumentQuarantinedEvent,
  DocumentScanCompletedEvent,
  FileRemovedEvent,
  InvalidFilesChangedEvent,
  InvalidFileWithError,
} from '@interfaces/mfe/mfe-document-upload.interface';
import { Store } from '@ngrx/store';
import { LoggerCodes, LoggerService } from '@shared/modules/logger/logger';
import { WindowRef } from '@shared/modules/window-ref/window-ref';
import { TrackProcessError } from '@store/actions/adobe-launch/adobe-launch.actions';
import {
  addWldDocument,
  loadWldConsentStatus,
  removeWldDocument,
  WldDocumentSlice,
} from '@store/actions/documents/documents.actions';
import type * as fromRootStore from '@store/index';
import { selectIsWldDocUploadEnabled } from '@store/selectors/document-upload/document-upload.selectors';
import { Observable } from 'rxjs';

export interface WldMfeHost {
  isDocumentUploaded: boolean;
  fileQuantityCheck: boolean;
  errorMessage: string[];
}

export interface SubSinkLike {
  add(...subscriptions: any[]): void;
}

export interface WldMfeHelperConfig {
  store: Store<fromRootStore.State>;
  loggerService: LoggerService;
  windowRef: WindowRef;
  subs: SubSinkLike;
  host: WldMfeHost;
  // Which bucket inside the WLD document store this page owns.
  slice: WldDocumentSlice;
  isInternal?: boolean;
  getErrorMessages?: () => Record<string, string> | undefined;
}

export class WldMfeHelper {
  isWldDocUploadEnabled$: Observable<boolean>;
  isWldDocUploadEnabled = false;
  invalidFiles: InvalidFileWithError[] = [];

  private consentStatusRequested = false;
  private config: WldMfeHelperConfig;

  constructor(config: WldMfeHelperConfig) {
    this.config = config;
    this.isWldDocUploadEnabled$ = this.createToggleObservable();
  }

  private createToggleObservable(): Observable<boolean> {
    return this.config.store.select(selectIsWldDocUploadEnabled);
  }

  init(): void {
    this.config.subs.add(
      this.isWldDocUploadEnabled$.subscribe((on) => {
        this.isWldDocUploadEnabled = on;
        if (on && !this.consentStatusRequested && !this.config.isInternal) {
          this.consentStatusRequested = true;
          this.config.store.dispatch(loadWldConsentStatus());
        }
      }),
    );
  }

  onInvalidFilesChanged(event: InvalidFilesChangedEvent): void {
    this.invalidFiles = event.detail || [];

    if (this.invalidFiles.length === 0) {
      this.config.host.errorMessage = [];
      return;
    }

    this.config.host.isDocumentUploaded = false;
    const KNOWN_ERROR_MAP: Record<string, { trackCode: string; contentKey: string }> = {
      FILE_SIZE_EXCEEDED: { trackCode: 'fileTooLarge', contentKey: 'fileTooLarge' },
      FILE_TYPE_INVALID: { trackCode: 'invalidFileType', contentKey: 'invalidFileType' },
      UPLOAD_FAILED: { trackCode: 'uploadFailed', contentKey: 'fileNotSaved' },
    };
    const errorTypes = this.invalidFiles.map((f) => f.errorType);
    const seen = new Set<string>();
    const messages: string[] = [];
    const errorMessages = this.config.getErrorMessages?.();
    for (const errorType of errorTypes) {
      if (!errorType || seen.has(errorType)) continue;
      seen.add(errorType);
      const mapped = KNOWN_ERROR_MAP[errorType];
      if (mapped) {
        this.adobeTrackError(mapped.trackCode);
        const message = errorMessages?.[mapped.contentKey];
        if (message) {
          messages.push(message);
        }
      } else {
        this.adobeTrackError(`unknown_${errorType.toLowerCase()}`);
        this.config.loggerService.info(
          {
            eventId: 'wld-mfe-unknown-invalid-file-error',
            message: `Unknown invalidFilesChanged errorType from WLD MFE: ${errorType}`,
          },
          LoggerCodes.NULL_FAILURE,
        );
      }
    }
    this.config.host.errorMessage = messages;
  }

  onDocumentScanCompleted(event: DocumentScanCompletedEvent): void {
    const file = event.detail;
    this.config.host.isDocumentUploaded = true;
    this.config.host.errorMessage = [];
    // fileQuantityCheck is derived from the wldDocuments count in the component,
    if (file?.documentId) {
      this.config.store.dispatch(addWldDocument({ slice: this.config.slice, document: file }));
    }
    this.config.loggerService.info(
      {
        eventId: 'wld-mfe-document-scan-completed',
        message: `WLD MFE document scan completed: ${file?.documentId}`,
      },
      LoggerCodes.GENERAL_SUCCESS,
    );
  }

  onDocumentQuarantined(event: DocumentQuarantinedEvent): void {
    const file = event.detail;
    this.config.host.isDocumentUploaded = false;
    // Removing the document recomputes fileQuantityCheck via the count-based rule.
    if (file?.documentId) {
      this.config.store.dispatch(
        removeWldDocument({ slice: this.config.slice, documentId: file.documentId }),
      );
    }
    this.config.loggerService.info(
      {
        eventId: 'wld-mfe-document-quarantined',
        message: `WLD MFE document quarantined: ${file?.documentId}`,
      },
      LoggerCodes.NULL_FAILURE,
    );
  }

  onFileRemoved(event: FileRemovedEvent): void {
    const file = event.detail;
    if (file?.documentId) {
      this.config.store.dispatch(
        removeWldDocument({ slice: this.config.slice, documentId: file.documentId }),
      );
    }
    this.config.host.isDocumentUploaded = false;
    // Removing the document recomputes fileQuantityCheck via the count-based rule.
    this.config.host.errorMessage = [];
    this.config.loggerService.info(
      {
        eventId: 'wld-mfe-file-removed',
        message: `WLD MFE file removed: ${file?.documentId}`,
      },
      LoggerCodes.GENERAL_SUCCESS,
    );
  }

  onNotAuthorizedRedirect(): void {
    this.config.loggerService.info(
      { eventId: 'wld-mfe-not-authorized', message: 'WLD MFE returned 401 - redirecting to login' },
      LoggerCodes.NULL_FAILURE,
    );
    this.config.windowRef.nativeWindow.location.href = '/logon';
  }

  private adobeTrackError(error: string): void {
    this.config.store.dispatch(
      TrackProcessError({
        errorCode: `coo_file_error_${error}`,
      }),
    );
  }
}
